import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ApiResponse, apiValue } from '@classes/api-response';

export interface CatalogField {
  key: string;
  label: string;
  required?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  private baseUrl = `${environment.base_url}/catalog`;

  constructor(private http: HttpClient) {}

  fields(): Observable<CatalogField[]> {
    return this.http.get(`${this.baseUrl}/fields`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'fields', [])),
      catchError(() => of([]))
    );
  }

  preview(vendorId: string, file: File, fieldMap: Record<string, string>): Observable<any> {
    const body = new FormData();
    body.append('file', file, file.name);
    body.append('fieldMap', JSON.stringify(fieldMap || {}));
    return this.http
      .post(`${this.baseUrl}/vendor/${vendorId}/preview`, body)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'preview', res)));
  }

  apply(
    vendorId: string,
    file: File,
    options: {
      fieldMap: Record<string, string>;
      acceptKeys?: string[];
      acceptAllChanges?: boolean;
      catalogRefreshMonths?: number;
    }
  ): Observable<any> {
    const body = new FormData();
    body.append('file', file, file.name);
    body.append('fieldMap', JSON.stringify(options.fieldMap || {}));
    body.append('acceptKeys', JSON.stringify(options.acceptKeys || []));
    body.append('acceptAllChanges', String(!!options.acceptAllChanges));
    if (options.catalogRefreshMonths != null) {
      body.append('catalogRefreshMonths', String(options.catalogRefreshMonths));
    }
    return this.http.post(`${this.baseUrl}/vendor/${vendorId}/apply`, body);
  }

  list(vendorId: string): Observable<Array<{
    _id: string;
    model: string;
    color: string;
    size: string;
    type?: string;
    vendorSku?: string;
  }>> {
    return this.http.get(`${this.baseUrl}/vendor/${vendorId}`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'catalog', [])),
      catchError(() => of([]))
    );
  }

  history(vendorId: string): Observable<any[]> {
    return this.http.get(`${this.baseUrl}/vendor/${vendorId}/history`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'imports', [])),
      catchError(() => of([]))
    );
  }

  alerts(store: string | number): Observable<any[]> {
    return this.http.get(`${this.baseUrl}/alerts/store/${store}`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'alerts', [])),
      catchError(() => of([]))
    );
  }

  dismiss(vendorId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/vendor/${vendorId}/dismiss`, {});
  }

  physicalFrames(productId: string): Observable<any[]> {
    return this.http.get(`${this.baseUrl}/product/${productId}/physical`).pipe(
      map((res: ApiResponse<any>) => apiValue(res, 'frames', [])),
      catchError(() => of([]))
    );
  }

  saveSettings(vendorId: string, settings: any): Observable<any> {
    return this.http
      .put(`${this.baseUrl}/vendor/${vendorId}/settings`, settings)
      .pipe(map((res: ApiResponse<any>) => apiValue(res, 'settings', {})));
  }
}
